// =========================================================================================@@
// Last Updated Date: Feb 3, 2022
// Last Updated By: Ajay
// Status Level: 3
// ===========================================================================================

import React, { useContext } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, Spinner, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import { MoreIcon } from 'assets/icons'
import BanUserConfirm from 'src/sites/kits/Account/components/BanUserConfirm'
import UserListItem from 'src/sites/kits/AdminWorkspace/components/UserListItem'
import UserBadgeManage from 'src/sites/kits/People/apps/UserBadgeManage'
import ModalRoute from 'src/sites/kits/Utils/ModalRoute'
import Popover from 'src/sites/kits/Utils/Popover'
import { useOrganization, useUserList, useRemoveUserFromRole, useResendRoleParticipationEmail }
   from 'src/core/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'

const RoleUserList = ({
   badgeId,
   roleId,
   searchValue,
   status,
   targetId,
   targetType
}) => {
   const match = useRouteMatch()
   const { refetchInitiative } = useContext(InitiativeHierarchyContext)
   const { organization, refetch: refetchOrganization } = useOrganization()

   const { removeUserFromRole } = useRemoveUserFromRole()
   const { resendRoleParticipationEmail } = useResendRoleParticipationEmail()
   const { userList, loading, fetchMore, refetch: refetchUsers } = useUserList({
      search: searchValue,
      userRelationshipFilter: {
         entityType: targetType,
         entityId: targetType === 'initiative'
            ? targetId
            : 'current',
         hasBadgeId: badgeId,
         hasRoleParticipantElement: { roleId, status }
      },
      withRelationship: true,
      withRelationshipEntityType: targetType,
      withRelationshipEntityId: targetType === 'initiative'
         ? targetId
         : 'current'
   })

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: userList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               userList: {
                  ...prevResult.userList,
                  items: [
                     ...prevResult.userList.items,
                     ...fetchMoreResult.userList.items
                  ],
                  listInfo: fetchMoreResult.userList.listInfo
               }
            }
         }
      })
   }

   const handleRemoveUserFromRole = async (userId) => {
      const proceed = window.confirm('Are you sure you want to remove this user?')
      if (!proceed) {
         return
      }

      try {
         await removeUserFromRole({
            roleId,
            userId,
            entityType: targetType,
            entityId: targetType === 'initiative'
               ? targetId
               : 'current'
         })

         refetchUsers()

         if (targetType === 'initiative') {
            refetchInitiative()
         } else {
            refetchOrganization()
         }
      } catch (err) {
         window.alert(err.message || 'Could not remove user')
      }
   }

   const handleResendEmail = async (relationshipId) => {
      try {
         await resendRoleParticipationEmail({
            relationshipId,
            roleId,
            useGatherCentralOrigin: process.env.APP_DIR === 'core'
         })

         window.alert('Email sent successfully')
      } catch (err) {
         window.alert(err.message || 'Could not send email')
      }
   }

   return (
      <Popover.Provider>
         <View float="left" width="100%">
            <View
               float="left"
               width="100%"
               backgroundColor="#fff"
               style={{
                  borderBottomLeftRadius: '6px',
                  borderBottomRightRadius: '6px'
               }}>
               <View
                  float="left"
                  width="100%"
                  padding="0px 12px">
                  {loading && (
                     <View display="flex" justifyContent="center" padding="60px">
                        <Spinner color="#ccc" width="20px" height="20px" />
                     </View>
                  )}
                  {!loading && userList.items.map((user, index) => {
                     const roleParticipantElement = user.relationship.elements.find(element => (
                        element.__typename === 'RelationshipElementRoleParticipant' &&
                        element.role.id === roleId
                     ))

                     return (
                        <UserListItem
                           key={user.id}
                           avatarMedia={user.avatarMedia}
                           badges={user.badges}
                           banButtonLinkTo={`${match.url}/ban-user`}
                           fullName={user.fullName}
                           status={roleParticipantElement.status}
                           borderTop={index === 0 ? 'none' : undefined}
                           action={(
                              <Popover.Anchor
                                 meta={{
                                    ...user,
                                    status: roleParticipantElement.status
                                 }}
                                 tabIndex={index}>
                                 <MoreIcon
                                    width="24px"
                                    height="24px"
                                    color="#aaa"
                                 />
                              </Popover.Anchor>
                           )}
                        />
                     )
                  })}
                  <Popover.View
                     anchorOriginHorizontal="right"
                     anchorOriginVertical="top"
                     width="180px">
                     {user => (
                        <View
                           float="left"
                           width="100%"
                           borderRadius="6px"
                           backgroundColor="#fff"
                           boxShadow="6px 6px 30px rgba(0,0,0,0.1)">
                           <ListMenu buttonSize="sm">
                              <ListMenuButton
                                 linkTo={`/-/profile/${user.id}`}
                                 name="View Profile"
                              />
                              {organization.currentUserCanEdit && (
                                 <ListMenuButton
                                    linkTo={`${match.url}/manage-badges/${user.id}`}
                                    name="Assign/Revoke Badges"
                                 />
                              )}
                              <ListMenuButton
                                 onClick={status === 'invited'
                                    ? () => handleResendEmail(user.relationship.id)
                                    : () => handleResendEmail(user.relationship.id)
                                 }
                                 name={`Resend ${status === 'active' ? 'Welcome' : 'Invite'} Email`}
                              />
                              {/* We can only ban active users */}
                              {organization.currentUserCanEdit && user.status === 'active' && (
                                 <ListMenuButton
                                    linkTo={`${match.url}/ban/${user.id}`}
                                    name="Ban this user"
                                 />
                              )}
                              <ListMenuButton
                                 onClick={() => handleRemoveUserFromRole(user.id)}
                                 name="Remove"
                              />
                           </ListMenu>
                        </View>
                     )}
                  </Popover.View>
                  {!loading && userList.items.length === 0 && (
                     <View padding="30px" textAlign="center">
                        <Text size="1.5" color="#888">
                           There are no users to show here
                        </Text>
                     </View>
                  )}
               </View>
            </View>
            {userList.listInfo.hasNext && (
               <View
                  float="left"
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  padding="12px 0px">
                  <Button
                     color="#000"
                     textColor="#fff"
                     size="xs"
                     onClick={handleLoadMore}
                     name="Load More"
                     mode={loading ? 'loading' : 'normal'}
                     rounded
                  />
               </View>
            )}
            <ModalRoute path={`${match.path}/manage-badges/:userId`}>
               {({ match: modalRouteMatch, open, onCloseComplete, onCloseTrigger }) => (
                  <Modal
                     borderRadius="5px"
                     width="90%[a-b] 500px[c-f]"
                     maxHeight="100%"
                     onCloseComplete={onCloseComplete}
                     onCloseTrigger={onCloseTrigger}
                     open={open}>
                     <UserBadgeManage
                        onCloseButtonClick={onCloseTrigger}
                        userId={modalRouteMatch.params.userId}
                     />
                  </Modal>
               )}
            </ModalRoute>
            <ModalRoute path={`${match.path}/ban/:userId`}>
               {({ match: modalRouteMatch, open, onCloseComplete, onCloseTrigger }) => (
                  <Modal
                     borderRadius="5px"
                     width="100%[a-b] 400px[c-f]"
                     maxHeight="100%"
                     onCloseComplete={onCloseComplete}
                     onCloseTrigger={onCloseTrigger}
                     open={open}>
                     <BanUserConfirm
                        onBanComplete={() => {
                           onCloseTrigger()
                           // TODO: this is a bit of a hack to get the list to refresh
                           // See PR #363
                           window.alert('User Ban successful')
                           window.location.assign(match.url)
                        }}
                        onCancelButtonClick={onCloseTrigger}
                        userId={modalRouteMatch.params.userId}
                     />
                  </Modal>
               )}
            </ModalRoute>
         </View>
      </Popover.Provider>
   )
}

RoleUserList.propTypes = {
   badgeId: PropTypes.string,
   roleId: PropTypes.string.isRequired,
   searchValue: PropTypes.string,
   status: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array
   ]),
   targetId: PropTypes.string,
   targetType: PropTypes.oneOf(['organization', 'initiative']).isRequired
}

RoleUserList.defaultProps = {
   badgeId: undefined,
   searchValue: null,
   status: 'active',
   targetId: undefined
}

export default React.memo(RoleUserList)
